import { useCallback, useEffect, useState } from 'react';

import { Button, Col, Form, Row, Typography, notification } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { simulationApi } from '@/api';
import IconClock from '@/assets/icons/icon-clock-circle.svg?react';
import IconID from '@/assets/icons/icon-id.svg?react';
import IconName from '@/assets/icons/icon-name.svg?react';
import IconPlants from '@/assets/icons/icon-plants.svg?react';
import IconScenarios from '@/assets/icons/icon-scenarios.svg?react';
import IconStorageSystems from '@/assets/icons/icon-storage-systems.svg?react';
import IconWind from '@/assets/icons/icon-wind.svg?react';
import Card from '@/components/RCard';
import RNotificationCard from '@/components/RNotificationCard';
import { CreateSimSteps } from '@/constants';
import { useAssets } from '@/hooks/useAssets';
import useAuth from '@/hooks/useAuth';
import useProductTourNewSimulation from '@/hooks/useProductTourNewSimulation';
import PlantDrawer from '@/pages/Plants/Drawer/PlantGenericDrawer';
import { DrawerType as DrawerTypePlant } from '@/pages/Plants/Drawer/types';
import ScenarioDrawer from '@/pages/Scenarios/Drawer/ScenarioGenericDrawer';
import StorageDrawer from '@/pages/StorageSystems/Drawer/StorageGenericDrawer';
import paths from '@/routes/paths';
import useProductTourStore from '@/store/useProductTourStore';
import { useSelectedAssetsStore } from '@/store/useSelectedAssetsStore';
import { DrawerType } from '@/types/global';
import { OverviewBodyItems, OverviewHeaderItems } from '@/types/overview';
import { Scenario } from '@/types/scenario';
import { SimulationsRaw } from '@/types/simulations';
import { StorageSystem } from '@/types/storageSystem';
import { handleError } from '@/utils';

import AssetsInformation from './AssetsInformation';
import PlantsStorageScenarioCollapse from './PlantsStorageScenarioCollapse';
import SimulationForm from './SimulationForm';
import StyledCol from './styles';
import RDrawer from '../RDrawer';
import Overview from '../ROverview';
import CustomSteps from '../RSteps';
import { RUsageLimitIndicator } from '../RUsageLimitIndicator';

type Props = {
  setNewSimulationDrawerVisible: (bool: boolean) => void;
  mutateFunction: () => void;
  isNewSimulationDrawerVisible: boolean;
  isLocked: boolean;
  runningSimulationsData?: SimulationsRaw;
};

const initialAsset = {
  name: '',
  id: '',
};

const NewSimulation: React.FC<Props> = ({
  setNewSimulationDrawerVisible,
  mutateFunction,
  isNewSimulationDrawerVisible,
  isLocked,
  runningSimulationsData,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const assetLimit = user?.organization.subscription.plan.simAssetLimit;

  // Steps state
  const [current, setCurrent] = useState<CreateSimSteps>(
    CreateSimSteps.DESC_OF_SIMULATION
  );
  const [notificationCardVisible, setNotificationCardVisible] = useState(false);

  // Form state
  // const [formState, setFormState] = useState({
  //   simulationName: '',
  //   simulationDescription: '',
  //   simulationStartDate: '',
  //   years: 1,
  // });
  const [form] = Form.useForm();
  const simulationName = form.getFieldValue('simulationName');
  const simulationDescription = form.getFieldValue('simulationDescription');
  const simulationStartDate = form.getFieldValue('simulationStartDate');
  const years = form.getFieldValue('years');

  // Validation state
  const [validation, setValidation] = useState({
    isFormValid: false,
    isAssetsValid: false,
    isCollapseKeysValid: false,
    nextButtonDisabled: true,
  });

  // Selected assets state
  const { selectedAssets, setSelectedAssets } = useSelectedAssetsStore();

  const {
    setAssets,
    plantsData,
    scenarioData,
    storageSystemData,
    plantsDataMutate,
    scenarioDataMutate,
    storageSystemMutate,
  } = useAssets(simulationStartDate, years);

  const [drawerPlant, setDrawerPlant] = useState<DrawerTypePlant>({
    type: null,
    data: null,
  });
  const [drawerScenario, setDrawerScenario] = useState<DrawerType<Scenario>>({
    type: null,
    data: null,
  });

  const [drawerStorage, setDrawerStorage] = useState<DrawerType<StorageSystem>>(
    {
      type: null,
      data: null,
    }
  );

  const { isTourActive } = useProductTourStore();

  useEffect(() => {
    plantsDataMutate();
    scenarioDataMutate();
    storageSystemMutate();
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [simulationStartDate, years]);

  // Validate next button
  useEffect(() => {
    setValidation((prev) => ({
      ...prev,
      nextButtonDisabled:
        (current === CreateSimSteps.DESC_OF_SIMULATION &&
          !(validation.isFormValid && validation.isAssetsValid)) ||
        (current === CreateSimSteps.ASSET_SELECTION &&
          !validation.isCollapseKeysValid) ||
        (current !== CreateSimSteps.DESC_OF_SIMULATION &&
          current !== CreateSimSteps.ASSET_SELECTION),
    }));
  }, [
    current,
    validation.isFormValid,
    validation.isAssetsValid,
    validation.isCollapseKeysValid,
  ]);

  const next = useCallback(() => {
    setCurrent((prev) => prev + 1);
    plantsDataMutate();
    scenarioDataMutate();
    storageSystemMutate();
  }, [plantsDataMutate, scenarioDataMutate, storageSystemMutate]);

  const prev = useCallback(() => setCurrent((prev) => prev - 1), []);

  // Handle simulation submission
  const submitSimulation = async () => {
    try {
      if (
        simulationName &&
        simulationStartDate &&
        selectedAssets.scenario.id &&
        selectedAssets.storageSystem.id
      ) {
        const values = form.getFieldsValue(true);
        const simulationData: {
          name: string;
          startDate: string;
          numYears: number;
          scenario: number;
          storage: number;
          marketTimeInterval: number;
          description: string;
          plants: number[];
        } = {
          name: values.simulationName,
          startDate: `${dayjs(values.simulationStartDate).format(
            'YYYY-MM-DD'
          )}T00:00:00.000Z`,
          numYears: values.years,
          scenario: parseInt(selectedAssets.scenario.id, 10),
          storage: parseInt(selectedAssets.storageSystem.id, 10),
          marketTimeInterval: 1,
          description: values.simulationDescription,
          plants: selectedAssets.plants
            ? selectedAssets.plants.map((asset) => +asset.id)
            : [],
        };

        await simulationApi.createSimulation(simulationData);
      }
      notification.success({
        message: t('components.newSimulation.notifications.simulationCreated'),
      });
      mutateFunction();
      const timer = isTourActive ? 0 : 1000;
      setTimeout(() => {
        navigate(paths.simulation.base);
        setNewSimulationDrawerVisible(false);
        resetData();
      }, timer);
    } catch (error: unknown) {
      handleError(error);
    }
  };

  const { simulationAssetsRef, reviewRef } = useProductTourNewSimulation({
    current,
    form,
    prev,
    next,
    submitSimulation,
  });

  // Handle form reset
  const resetData = useCallback(() => {
    setCurrent(CreateSimSteps.DESC_OF_SIMULATION);
    setAssets({ plants: [], scenarios: [], storageSystems: [] });
    setValidation({
      isFormValid: false,
      isAssetsValid: false,
      isCollapseKeysValid: false,
      nextButtonDisabled: true,
    });
    form.setFieldsValue({
      simulationName: '',
      simulationDescription: '',
      simulationStartDate: '',
      years: 1,
    });
    setSelectedAssets({
      plants: [],
      scenario: initialAsset,
      storageSystem: initialAsset,
    });
  }, []);

  const overviewParameters: OverviewHeaderItems[] = [
    {
      label: t('components.newSimulation.overviewParameters.name'),
      icon: <IconName fill={theme.colors.grayAccent} />,
      value: simulationName,
    },
    {
      label: t('components.newSimulation.overviewParameters.description'),
      icon: <IconID fill={theme.colors.grayAccent} />,
      value: simulationDescription,
    },
    {
      label: t('components.newSimulation.overviewParameters.startDate'),
      icon: <IconClock fill={theme.colors.grayAccent} />,
      value: dayjs(simulationStartDate).format('YYYY-MM-DD'),
    },
    {
      label: t('components.newSimulation.overviewParameters.year'),
      icon: <IconClock fill={theme.colors.grayAccent} />,
      value: years,
    },
  ];

  const overviewBody: OverviewBodyItems[] = [
    {
      label: t('components.newSimulation.overviewBody.plant'),
      icon: (
        <IconPlants
          fill={theme.colors.grayAccent}
          height={'32px'}
          width={'32px'}
        />
      ),
      buttons: selectedAssets.plants.map((plant) => ({
        label: plant.name,
        icon: <IconWind fill="black" />,
        onClickFunction: () => {
          setDrawerPlant({ type: 'view', data: plant });
        },
      })),
    },
    {
      label: t('components.newSimulation.overviewBody.scenario'),
      icon: (
        <IconScenarios
          fill={theme.colors.grayAccent}
          height={'32px'}
          width={'32px'}
        />
      ),
      buttons: [
        {
          label: selectedAssets.scenario.name,
          icon: <IconWind fill="black" />,
          onClickFunction: () => {
            setDrawerScenario({ type: 'view', data: selectedAssets.scenario });
          },
        },
      ],
    },
    {
      label: t('components.newSimulation.overviewBody.storageSystem'),
      icon: (
        <IconStorageSystems
          fill={theme.colors.grayAccent}
          height={'32px'}
          width={'32px'}
          style={{ marginTop: '4px' }}
        />
      ),
      buttons: [
        {
          label: selectedAssets.storageSystem.name,
          icon: <IconWind fill="black" width={14} height={14} />,
          onClickFunction: () => {
            setDrawerStorage({
              type: 'view',
              data: selectedAssets.storageSystem,
            });
          },
        },
      ],
    },
  ];

  const steps = [
    {
      title: t('components.newSimulation.steps.descriptionOfSimulation'),
      content: (
        <SimulationForm
          form={form}
          setIsFormValid={(isValid) =>
            setValidation((prev) => ({ ...prev, isFormValid: isValid }))
          }
        />
      ),
    },
    {
      title: t('components.newSimulation.steps.assetSelection'),
      content: (
        <PlantsStorageScenarioCollapse
          setIsCollapseKeysValid={(check) =>
            setValidation((prev) => ({ ...prev, isCollapseKeysValid: check }))
          }
          current={current}
          form={form}
        />
      ),
    },
    {
      title: t('components.newSimulation.steps.overview'),
      content: (
        <>
          <Typography.Text className="fs-14-bold">
            {t('components.newSimulation.steps.overview')}
          </Typography.Text>
          <br />
          <Typography.Text className="fs-14-regular dark-6">
            {t('components.newSimulation.overviewDescription')}
          </Typography.Text>
          <div ref={reviewRef}>
            <Overview parameters={overviewParameters} body={overviewBody} />
          </div>
        </>
      ),
    },
  ];

  const items = steps.map((item) => ({ key: item.title, title: item.title }));

  // Determine if there is unsaved data
  const hasUnsavedData = () => {
    return (
      simulationName ||
      simulationDescription ||
      simulationStartDate ||
      selectedAssets.plants.length ||
      selectedAssets.scenario.id ||
      selectedAssets.storageSystem.id
    );
  };

  const handleNewSimulationDrawerClose = () => {
    if (hasUnsavedData()) {
      setNotificationCardVisible(true);
    } else {
      setNewSimulationDrawerVisible(false);
    }
  };

  return (
    <RDrawer
      open={isNewSimulationDrawerVisible}
      destroyOnClose
      onClose={handleNewSimulationDrawerClose}
      footer={false}
      delayMask={true}
      drawerLock={{
        isLocked,
        lockText: t('components.newSimulation.lockText'),
      }}
      title={t('components.newSimulation.title')}
      description={t('components.newSimulation.description')}
      rightSideSection={
        <RUsageLimitIndicator
          text={t('components.newSimulation.remainingSimulations')}
          currentValue={runningSimulationsData?.count}
          maxValue={
            user?.organization.subscription.plan.name.toLocaleLowerCase() ==
            'enterprise'
              ? Infinity
              : user?.organization.subscription.plan.simultaneousSimLimit
          }
        />
      }
    >
      <StyledCol>
        <Card className="pt-16 px-16 pb-12 content-container mt-24">
          <Col className="steps">
            <CustomSteps current={current} items={items} type="default" />
          </Col>

          <Card className="pt-16 px-16 current-card mt-24">
            {steps[current].content}
          </Card>
          {current == CreateSimSteps.DESC_OF_SIMULATION &&
            years != 0 &&
            simulationStartDate !== '' &&
            plantsData != undefined &&
            scenarioData != undefined &&
            storageSystemData != undefined && (
              <Card className="py-16 px-16 mt-24">
                <div className="cards" ref={simulationAssetsRef}>
                  <AssetsInformation
                    form={form}
                    setIsAssetsValid={(check) =>
                      setValidation((prev) => ({
                        ...prev,
                        isAssetsValid: check,
                      }))
                    }
                  />
                </div>
              </Card>
            )}

          <Row
            className="bottom-button-group"
            align="middle"
            justify="space-between"
          >
            <Button
              className="btn-cancel"
              onClick={handleNewSimulationDrawerClose}
            >
              {t('components.newSimulation.buttons.cancel')}
            </Button>
            <div className="right-side-btn-group">
              {current > CreateSimSteps.DESC_OF_SIMULATION && (
                <Button
                  className="btn-back"
                  type="default"
                  onClick={() => prev()}
                >
                  {t('components.newSimulation.buttons.previous')}
                </Button>
              )}

              {current < steps.length - 1 && (
                <Button
                  className="btn-next"
                  type="primary"
                  onClick={() => next()}
                  disabled={validation.nextButtonDisabled}
                >
                  {t('components.newSimulation.buttons.next')}
                </Button>
              )}
              {current === steps.length - 1 && (
                <Button
                  type="primary"
                  className="btn-next"
                  onClick={() => submitSimulation()}
                >
                  {t('components.newSimulation.buttons.create')}
                </Button>
              )}
            </div>
          </Row>
        </Card>
        <RNotificationCard
          title={t('components.newSimulation.notificationCard.title')}
          message={t('components.newSimulation.notificationCard.message')}
          type="warning"
          confirmText={t(
            'components.newSimulation.notificationCard.confirmText'
          )}
          cancelText={t('components.newSimulation.notificationCard.cancelText')}
          open={notificationCardVisible}
          onConfirm={() => {
            resetData();
            setNotificationCardVisible(false);
            setNewSimulationDrawerVisible(false);
          }}
          onCancel={() => setNotificationCardVisible(false)}
        />
        {assetLimit && plantsData && (
          <PlantDrawer
            drawerOptions={drawerPlant}
            setDrawerOptions={setDrawerPlant}
          />
        )}
        {assetLimit && scenarioData && (
          <ScenarioDrawer
            drawerOptions={drawerScenario}
            setDrawerOptions={setDrawerScenario}
          />
        )}
        {assetLimit && storageSystemData && (
          <StorageDrawer
            drawerOptions={drawerStorage}
            setDrawerOptions={setDrawerStorage}
          />
        )}
      </StyledCol>
    </RDrawer>
  );
};

export default NewSimulation;
